import React, { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useDispatch, useSelector } from "react-redux";
import {
  getTemplateData,
  sendEmails,
  uploadSubscribers,
} from "../redux/emailSlice";
import { fetchScraperCampaignSummaries } from "../redux/scraperSlice";
import { toast } from "react-toastify";
import EmailTemplatePreview from "../userTemplateView/EmailTemplatePreview"; // Import the template preview component
import ReactQuill from "react-quill"; // Import Quill editor
import "react-quill/dist/quill.snow.css"; // Import Quill CSS for styling
import juice from "juice";
import Papa from "papaparse"; // For CSV parsing
import * as XLSX from "xlsx"; // For Excel parsing
import { useRef } from "react";

const ArtistTemplateSelector = () => {
  const dispatch = useDispatch();
  const { templates, isLoading } = useSelector((state) => state.email);
  const { scrapingCampaignSummaries } = useSelector((state) => state.scraper);
  const [openCallId, setOpenCallId] = useState();
  const [numUsers, setNumUsers] = useState(1);
  const [numOrganizations, setNumOrganizations] = useState(1);
  const [numReviews, setNumReviews] = useState(1);
  const [numArtistOfMonth, setNumArtistOfMonth] = useState(1);
  const [numOpenCalls, setNumOpenCalls] = useState(1);
  const [numSubscribers, setNumSubscribers] = useState(1);
  const [emailSubject, setEmailSubject] = useState(
    "Exciting New Opportunities for Artists!"
  );
  const [emailCampaign, setEmailCampaign] = useState("Open Call Campaign");
  const [selectedTemplate, setSelectedTemplate] = useState("template2"); // Default template selection
  const [customContent, setCustomContent] = useState(""); // Store editor content
  const [isUploading, setIsUploading] = useState(false); // Loader state for file upload
  const [uploadMessage, setUploadMessage] = useState(
    "Uploading... Please Wait!"
  );
  const [batchSize, setBatchSize] = useState(50);
  const [totalEmails, setTotalEmails] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [showStartModal, setShowStartModal] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState("");
  const [campaignResponseMessage, setCampaignResponseMessage] = useState("");

  const dropdownRef = useRef();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatSecondsToHMS = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h > 0 ? `${h}h ` : ""}${m > 0 ? `${m}m ` : ""}${s}s`;
  };

  useEffect(() => {
    dispatch(fetchScraperCampaignSummaries());
  }, [dispatch]);

  const toggleCampaignSelection = (campaign) => {
    setSelectedCampaigns((prev) =>
      prev.includes(campaign)
        ? prev.filter((c) => c !== campaign)
        : [...prev, campaign]
    );
  };

  // Fetch data when the component loads
  useEffect(() => {
    dispatch(
      getTemplateData({
        num_users: numUsers,
        num_organizations: numOrganizations,
        num_reviews: numReviews,
        num_open_calls: numOpenCalls,
        num_subscribers: numSubscribers,
        num_artists_of_month: numArtistOfMonth,
        opencall_id: openCallId,
      })
    );
  }, [
    numUsers,
    numOrganizations,
    numReviews,
    numOpenCalls,
    numSubscribers,
    numArtistOfMonth,
    openCallId,
    dispatch,
  ]);

  // File upload handling logic remains unchanged
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    setIsUploading(true);
    setUploadMessage("Please wait! Checking Uploaded File...");

    const processAndUploadData = (data) => {
      const validData = data.filter((row) => row.email && row.status);

      if (validData.length === 0) {
        toast.error(
          "No valid subscribers found. Please check your file format."
        );
        setIsUploading(false);
        return;
      }

      dispatch(uploadSubscribers(validData)).then((response) => {
        setUploadMessage(
          `Upload complete! ${response.payload.insertedCount} records inserted.`
        );
        setTimeout(() => {
          setIsUploading(false);
        }, 2000);
      });
    };

    if (fileExtension === "csv") {
      console.log("Processing CSV file...");
      Papa.parse(file, {
        header: true,
        complete: (result) => {
          console.log("CSV Parsed Data:", result.data);
          processAndUploadData(result.data);
        },
        error: (error) => {
          console.error("CSV Parsing Error:", error);
          setIsUploading(false);
          toast.error("Error processing CSV file.");
        },
      });
    } else if (fileExtension === "xlsx" || fileExtension === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          console.log("Processing Excel file...");
          const workbook = XLSX.read(e.target.result, { type: "binary" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(firstSheet);
          console.log("Excel Parsed Data:", data);
          processAndUploadData(data);
        } catch (error) {
          console.error("Error reading Excel file:", error);
          toast.error("Error processing Excel file.");
        }
      };
      reader.readAsBinaryString(file);
    } else {
      setIsUploading(false);
      toast.error("Unsupported file type. Please upload a CSV or Excel file.");
    }
  };

  const handleSendEmail = () => {
    const htmlContent = ReactDOMServer.renderToString(
      <EmailTemplatePreview
        selectedTemplate={selectedTemplate}
        data={templates}
        customContent={customContent}
      />
    );
    const htmlBodyWithInlineStyles = juice(htmlContent);

    const payload = {
      keys: selectedCampaigns, // example: ["native_artists", "spring2025", "native_subscribers"]
      subject: emailSubject,
      html_body: htmlBodyWithInlineStyles,
      campaign_name: emailCampaign,
      batch_count: batchSize,
    };

    dispatch(sendEmails(payload))
      .then((res) => {
        const { total_emails, total_batches, estimated_duration_seconds, message } =
          res.payload;

        // Show initial popup
        setCampaignResponseMessage(message ? message : "");
        setEstimatedTime(formatSecondsToHMS(estimated_duration_seconds));
        setShowStartModal(true);
        setTimeout(() => {
          setShowStartModal(false);
        }, 5000); // Hide after 5 seconds
        setTotalEmails(total_emails);
        setTotalBatches(total_batches);
      })
      .catch((error) => {
        console.error("Failed to send emails:", error);
        toast.error("Failed to send emails.");
      });
  };

  const campaignOptions = [
    {
      label: `Registered Artists (${
        scrapingCampaignSummaries?.total_artists || 0
      })`,
      value: "native_artists",
    },
    {
      label: `Registered Organizations (${
        scrapingCampaignSummaries?.total_organizations || 0
      })`,
      value: "native_organizations",
    },
    {
      label: `Registered Subscribers (${
        scrapingCampaignSummaries?.total_subscribers || 0
      })`,
      value: "native_subscribers",
    },
    ...(scrapingCampaignSummaries?.scraping_summary || []).map((summary) => ({
      label: `${summary.clean_campaign} (${summary.total_emails ?? 0})`,
      value: summary.clean_campaign,
    })),
  ];

  return (
    <div className="p-5">
      <h1 className="text-2xl font-bold mb-4">
        Select a Template and Send Emails
      </h1>

      {/* File upload */}
      <div className="mb-4">
        <label className="block mb-2">
          Upload Recipient List (CSV or Excel):
        </label>
        <input
          type="file"
          onChange={handleFileUpload}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0
                   file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        {isUploading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-3/4 md:w-1/2 lg:w-1/3">
              <p className="mb-4 text-center text-lg font-medium text-gray-700">
                {uploadMessage}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div className="bg-blue-600 h-4 rounded-full transition-all duration-300"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="block mb-2">Select Template:</label>
        <select
          value={selectedTemplate}
          onChange={(e) => setSelectedTemplate(e.target.value)}
          className="border p-2 rounded w-full"
        >
          <option value="template1">
            Template 1 (Artists & Organizations)
          </option>
          <option value="template2">Template 2 (Open Calls & Reviews)</option>
          <option value="template3">
            Template 3 (Open Calls With Deadline Expires Soon)
          </option>
          <option value="template4">
            Template 4 (Recently Added Open Calls)
          </option>
          <option value="template5">Template 5 (Most Viewed Open Calls)</option>
          <option value="template6">Template 6 (Featured Open Call)</option>
          <option value="template7">Template 7 (Artist Of The Month)</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block mb-2">Batch Size:</label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={batchSize}
          onChange={(e) => setBatchSize(parseInt(e.target.value, 10) || 1)}
          placeholder="Enter batch size"
        />
      </div>

      {/* Text Editor */}
      <div className="mb-4">
        <label className="block mb-2">Add Custom Content:</label>
        <ReactQuill
          value={customContent}
          onChange={setCustomContent}
          theme="snow"
          modules={{
            toolbar: [
              [{ header: "1" }, { header: "2" }, { font: [] }],
              [{ list: "ordered" }, { list: "bullet" }],
              ["bold", "italic", "underline"],
              [{ align: [] }],
              ["link", "image"],
              [{ color: [] }, { background: [] }],
              [{ script: "sub" }, { script: "super" }],
              ["blockquote", "code-block"],
              [{ direction: "rtl" }],
            ],
          }}
          className="w-full border rounded"
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2">
          Enter Specific Open Call's Id To Fetch and Send
        </label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={openCallId}
          onChange={(e) => setOpenCallId(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2">
          How many number of Artists want to fetch?
        </label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={numUsers}
          onChange={(e) => setNumUsers(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2">
          How many number of "Organizations" want to fetch?
        </label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={numOrganizations}
          onChange={(e) => setNumOrganizations(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2">
          How many number of "Artist Of The Month" want to fetch?
        </label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={numArtistOfMonth}
          onChange={(e) => setNumArtistOfMonth(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2">
          How many number of "Reviews" want to fetch?
        </label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={numReviews}
          onChange={(e) => setNumReviews(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2">
          How many number of "OpenCalls" want to fetch?
        </label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={numOpenCalls}
          onChange={(e) => setNumOpenCalls(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2">
          How many number of "Subscribers" want to fetch?
        </label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={numSubscribers}
          onChange={(e) => setNumSubscribers(e.target.value)}
        />
      </div>

      {/* Template Preview */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-2">
          Preview Selected Template
        </h2>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <EmailTemplatePreview
            selectedTemplate={selectedTemplate}
            data={templates}
            customContent={customContent}
          />
        )}
      </div>

      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-2">
          Campaign Name - Try to keep it unique everytime
        </h2>
        <input
          type="text"
          className="border p-2 rounded w-full"
          value={emailCampaign}
          onChange={(e) => setEmailCampaign(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-2">Email Subject</h2>
        <input
          type="text"
          className="border p-2 rounded w-full"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
        />
      </div>

      <div className="mb-3 relative" ref={dropdownRef}>
        <label className="block mb-1 font-medium">Select Campaigns</label>

        <div
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="border border-gray-300 bg-white p-2 rounded flex justify-between items-center cursor-pointer"
        >
          <span className="text-gray-700">
            {selectedCampaigns.length > 0
              ? `${selectedCampaigns.length} selected`
              : "Select campaigns"}
          </span>
        </div>

        {isDropdownOpen && (
          <div className="absolute mt-1 w-full bg-white border border-gray-300 rounded shadow max-h-60 overflow-y-auto z-50">
            {campaignOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedCampaigns.includes(option.value)}
                  onChange={() => toggleCampaignSelection(option.value)}
                  className="mr-2"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="mb-4 mt-8 relative group">
        <button
          className={`px-4 py-2 rounded text-white ${
            selectedCampaigns.length === 0
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-700"
          }`}
          disabled={selectedCampaigns.length === 0}
          onClick={handleSendEmail}
        >
          Send Now
        </button>

        {selectedCampaigns.length === 0 && (
          <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black text-white text-sm rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
            Select at least one campaign to enable
          </div>
        )}
      </div>

      {showStartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 shadow-2xl text-center w-96">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">
              {campaignResponseMessage}
            </h2>
            <p className="text-lg text-gray-600">
              Estimated time{" "}
              <span className="font-bold text-blue-600">
                {estimatedTime ? estimatedTime : ""}
              </span>{" "}
              to send{" "}
              <span className="font-bold text-blue-600">
                {totalEmails ? totalEmails : ""}
              </span>{" "}
              Emails
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistTemplateSelector;
